<template>
  <div class="revenue-comparison" data-cy="membership-revenue-comparison">
    <Skeleton
      v-if="showSkeleton"
      width="100%"
      height="6.5rem"
      data-cy="membership-revenue-comparison-skeleton"
    />
    <div v-else class="revenue-comparison__card">
      <header class="revenue-comparison__head">
        <div class="revenue-comparison__heading">
          <span class="revenue-comparison__eyebrow">Trailing 12 Months</span>
          <div class="revenue-comparison__title-row">
            <h3 class="revenue-comparison__title">Member Value</h3>
            <Tooltip placement="top" :width="320">
              <template #trigger>
                <i
                  class="fas fa-info-circle revenue-comparison__info"
                  aria-label="More information"
                ></i>
              </template>
              <template #tooltip>
                <span class="revenue-comparison__tooltip-text">
                  {{ TOOLTIP }}
                </span>
              </template>
            </Tooltip>
          </div>
          <p
            v-if="ratioSummary"
            class="revenue-comparison__lead"
            data-cy="revenue-comparison-ratio-summary"
          >
            {{ ratioSummary }}
          </p>
        </div>
      </header>
      <div class="revenue-comparison__columns">
        <div
          class="revenue-comparison__col"
          data-cy="revenue-comparison-member"
        >
          <span class="revenue-comparison__col-label">Members</span>
          <div class="revenue-comparison__stats">
            <div class="revenue-comparison__stat">
              <span class="revenue-comparison__stat-label">Avg Invoice</span>
              <span
                class="revenue-comparison__stat-value"
                data-cy="revenue-comparison-member-avg-invoice"
                >{{ memberAvgInvoice }}</span
              >
            </div>
            <div class="revenue-comparison__stat">
              <span class="revenue-comparison__stat-label"
                >Avg Jobs/Customer</span
              >
              <span
                class="revenue-comparison__stat-value"
                data-cy="revenue-comparison-member-avg-jobs"
                >{{ memberAvgJobs }}</span
              >
            </div>
          </div>
        </div>
        <div
          class="revenue-comparison__col"
          data-cy="revenue-comparison-non-member"
        >
          <span class="revenue-comparison__col-label">Non-Members</span>
          <div class="revenue-comparison__stats">
            <div class="revenue-comparison__stat">
              <span class="revenue-comparison__stat-label">Avg Invoice</span>
              <span
                class="revenue-comparison__stat-value"
                data-cy="revenue-comparison-non-member-avg-invoice"
                >{{ nonMemberAvgInvoice }}</span
              >
            </div>
            <div class="revenue-comparison__stat">
              <span class="revenue-comparison__stat-label"
                >Avg Jobs/Customer</span
              >
              <span
                class="revenue-comparison__stat-value"
                data-cy="revenue-comparison-non-member-avg-jobs"
                >{{ nonMemberAvgJobs }}</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useStore } from "vuex";

import Tooltip from "@/components/Tooltip.vue";
import Skeleton from "./Skeleton.vue";
import type { MembershipRevenueComparison } from "./types";

const TOOLTIP =
  "Compares job revenue and visit frequency for member vs. non-member customers over the last 12 months. Avg Invoice = Total Revenue ÷ Total Jobs. Avg Jobs/Customer = Total Jobs ÷ Unique Customers.";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD"
});

const store = useStore();

const data = computed<MembershipRevenueComparison | null>(
  () => store.state.membershipDashboardRevenueComparison
);
const loading = computed<boolean>(
  () => store.state.membershipDashboardRevenueComparisonLoading
);
const showSkeleton = computed<boolean>(
  () => loading.value || data.value === null
);

function formatCurrency(cents: number | undefined | null): string {
  return currencyFormatter.format((cents ?? 0) / 100);
}

function formatJobs(value: number | undefined | null): string {
  return Number(value ?? 0).toFixed(1);
}

const memberAvgInvoice = computed(() =>
  formatCurrency(data.value?.member?.avg_invoice_cents)
);
const memberAvgJobs = computed(() =>
  formatJobs(data.value?.member?.avg_jobs_per_customer)
);
const nonMemberAvgInvoice = computed(() =>
  formatCurrency(data.value?.non_member?.avg_invoice_cents)
);
const nonMemberAvgJobs = computed(() =>
  formatJobs(data.value?.non_member?.avg_jobs_per_customer)
);

const ratioSummary = computed<string | null>(() => {
  const member = data.value?.member;
  const nonMember = data.value?.non_member;
  if (!member || !nonMember) return null;
  if (!nonMember.avg_invoice_cents || !nonMember.avg_jobs_per_customer) {
    return null;
  }
  if (!member.avg_invoice_cents || !member.avg_jobs_per_customer) return null;

  const invoiceRatio = member.avg_invoice_cents / nonMember.avg_invoice_cents;
  const jobsRatio =
    member.avg_jobs_per_customer / nonMember.avg_jobs_per_customer;
  if (invoiceRatio <= 1 || jobsRatio <= 1) {
    return null;
  }

  const invoiceLift =
    invoiceRatio < 2
      ? `${Math.round((invoiceRatio - 1) * 100)}% more`
      : `${invoiceRatio.toFixed(1)}x as much`;
  const jobsLift =
    jobsRatio < 2
      ? `${Math.round((jobsRatio - 1) * 100)}% more often`
      : `${jobsRatio.toFixed(1)}x as often`;

  return `Members spend ${invoiceLift} per invoice and visit ${jobsLift}.`;
});

onMounted(() => {
  store.dispatch("fetchMembershipDashboardRevenueComparison");
});
</script>

<style lang="scss" scoped>
.revenue-comparison {
  width: 100%;
}
.revenue-comparison__card {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #fff;
  border: 1px solid var(--medium-color, #e5e7eb);
  border-radius: 6px;
  padding: 1.25rem;
  height: 100%;
  min-height: 6.5rem;
}
.revenue-comparison__head {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}
.revenue-comparison__heading {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}
.revenue-comparison__eyebrow {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #4b5563;
}
.revenue-comparison__title-row {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.revenue-comparison__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}
.revenue-comparison__info {
  font-size: 0.75rem;
  color: #9ca3af;
  cursor: help;
  transition: color 120ms ease;
  &:hover {
    color: #4b5563;
  }
}
.revenue-comparison__tooltip-text {
  font-size: 0.8125rem;
  line-height: 1.4;
}
.revenue-comparison__lead {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: #4b5563;
}
.revenue-comparison__columns {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}
@media (min-width: 480px) {
  .revenue-comparison__columns {
    grid-template-columns: 1fr 1fr;
  }
}
.revenue-comparison__col {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0;
  & + & {
    border-top: 1px solid var(--medium-color, #e5e7eb);
    padding-top: 0.75rem;
  }
}
@media (min-width: 480px) {
  .revenue-comparison__col + .revenue-comparison__col {
    border-top: none;
    border-left: 1px solid var(--medium-color, #e5e7eb);
    padding-top: 0.5rem;
    padding-left: 1rem;
  }
}
.revenue-comparison__col-label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}
.revenue-comparison__stats {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.revenue-comparison__stat {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}
.revenue-comparison__stat-label {
  font-size: 0.75rem;
  color: #6b7280;
}
.revenue-comparison__stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
</style>
